<script>
/**
 * O cartão da SIMULAÇÃO dentro do thread (spec-007, [T107] — US-2 / US-2b).
 *
 * Ele monta os componentes de verdade do Fee Lab a partir do `S` que a tool
 * devolveu — os MESMOS que a tela de simulação usa, com as mesmas props. Não é
 * economia de código: é o que impede o chat e a tela de contarem histórias
 * diferentes sobre o mesmo cenário. Nenhum número aqui é derivado no template.
 *
 * ★ **CALCULA, não busca.** O `simulate()` é função pura e o `S` veio pronto no
 * `tool_result`; a ponte (`utils/simulacao-do-chat.js`) resolve motor + guarda
 * numa decisão só, antes de qualquer componente montar.
 *
 * ★ **A guarda de plausibilidade vem ANTES de desenhar.** Com um `S` faltando
 * `rent`/`alfa`/`aporteAnual`, os custos em BPS saem CERTOS e todo valor em
 * REAIS sai `NaN` — que vira `null` ao serializar. Publicar isso é pior que não
 * publicar: os bps sustentam a leitura de que o cenário saiu inteiro, e o que
 * falta é exatamente o que o consultor mostra ao cliente. Sem os dois valores,
 * **nenhum número vai à tela** e a frase diz que o cálculo não fechou.
 *
 * ★ **Sem o grupo 4, o gráfico de evolução não é publicado** — e isso NÃO é
 * "publica vazio": `simulateEvolucaoPatrimonio` degrada para UM ponto sem
 * estourar, e um gráfico de um ponto afirma "este cliente não evolui", que é
 * uma afirmação sobre a vida dele que ninguém apurou.
 *
 * ★ **`previewId` vazio esconde o Salvar.** O commit confere o id contra o
 * registro do servidor e recusa sem ele; oferecer o botão aí é beco — o
 * consultor clica, espera e leva um não. Mesma decisão do
 * `fee-fixo-guia-confirmacao` com o `semRegistro`, e o **Cancelar continua
 * vivo**, que é a saída que sobra.
 *
 * ★ **O `data-teste` da RAIZ é do CONSUMIDOR, não deste arquivo** — e isto é
 * decisão, não esquecimento. Em Vue 2, atributo não-prop declarado por quem
 * monta VENCE o do filho na raiz: com os dois, o unit isolado passa e o
 * seletor na tela montada falha por "não achei", com tudo funcionando (medido
 * na spec-006). Aqui a raiz não leva marcador; os elementos internos levam, com
 * o prefixo `simulacao-`.
 *
 * ★ **[owner, 19/09/2026] Ele herda a MOLDURA da família, e mantém a casca
 * PRÓPRIA — as duas coisas.** Era um `<div>` cru: sem caixa, sem sobretítulo,
 * com o título num rótulo miúdo e o glifo em `#8E8E8E` literal. Hoje a raiz é
 * `ia-block-card pa-4` (hairline, `--radius-lg`, branco, sem sombra) e o
 * cabeçalho é o par `MÓDULO · SEÇÃO` + título em serif, como nos ~24 cartões
 * de leitura.
 *
 * ⚠️ **E por que ele NÃO virou `CartaoCasca`**, ao contrário de
 * `cartao-apresentacao`, `cartao-deck-r2-protocolo` e `cartao-recorte`:
 *
 * 1. A casca é a moldura dos cartões de LEITURA, com um corpo e o rodapé
 *    "Mostrando N de M · abrir na tela". Este é um PAINEL de decisão — KPIs,
 *    regimes, pizzas de alocação, projeção e a tabela da migração —, e cada um
 *    desses blocos já traz a própria moldura. Dentro do corpo da casca eles
 *    viram cartão-dentro-de-cartão em cinco níveis.
 * 2. O `sem-borda` da casca resolveria a caixa, mas não o disclosure: recolher
 *    um painel deste tamanho por um chevron de 14px no canto é um gesto que
 *    ninguém pediu, e o cartão é a superfície do two-step — recolhido, esconde
 *    o botão que autoriza a gravação.
 * 3. Ele não é só do chat. A mesma instância é montada pela tela do Guia e pelo
 *    drawer do Especialista; a casca levaria o disclosure e o rodapé para as
 *    duas, que não foi o que o owner olhou.
 *
 * O que SE migra é o vocabulário: moldura, sobretítulo e título — que é o que
 * fazia o cartão parecer de outra família na vitrine.
 *
 * ★ **NÃO deposita.** O depósito dos `resultados` (G-7, T037) tem um dono só —
 * `apiFeeFixoIa.depositarSimulacao`, chamado pelo captador de `tool_result`
 * (drawer e guia) ou pelo embrulho `ia/cartoes/cartao-simulacao.js` (chat
 * imersivo). Depositar aqui repetia o POST a cada remount (F5 com
 * histórico no chat imersivo, troca de aba que destrói o drawer) e pintava
 * "erro de depósito" sobre cartão histórico já decidido. O que sobra é a
 * apresentação: `erroDeposito` chega por prop e o retry sai como `redepositar`.
 *
 * ★ **`avisoCarteiraPendente` é a faixa ACIMA dos KPIs** (G-22 rodada 2, QA
 * `probe-g22`, 07/09/2026). O backend já publicava
 * `value.aviso_carteira_pendente` quando a simulação rodou sobre uma carteira
 * mais antiga que um upload pendente de confirmação — e nada aqui desenhava
 * esse campo: o cartão mostrava os números como se a carteira estivesse em
 * dia, e a fala do agente também não repetia o aviso (confiar no modelo
 * relatar um campo do `value` não fecha). O texto é do BACKEND, verbatim — a
 * tela não reescreve.
 *
 * ★ **As duas pizzas de alocação (hoje × fim da migração) entram ENTRE
 * `FeeLabRegimeCards` e `FeeLabProjecaoChart`** (F5, pedido do owner,
 * 08/09/2026). A ordem espelha a do PDF (`ff-alocacao.html`, comentário de
 * `ORDEM_PAGINAS` no backend): "alocação entra DEPOIS da composição do custo
 * e ANTES da evolução no tempo" — no cartão, `KpiStrip`+`RegimeCards` fazem o
 * papel de "composição/comparação de regime" e `ProjecaoChart`+
 * `EvolucaoPatrimonio` fazem o de "evolução no tempo". `utils/
 * alocacao-comparada-do-chat.js` lê `inputs.alocacao.alc_c`/`inputs.alloc` —
 * o MESMO par que `_alocacao_comparada` lê do lado do PDF — e degrada como
 * ele: sem as duas alocações fechando a soma, o bloco INTEIRO some, nunca
 * meia pizza ou uma fatia zerada inventada (um gráfico com dado ausente
 * mente para o consultor). Não calcula CUSTO por classe (isso depende do
 * fator de cashback do canal, cálculo do motor Python, fora do `S` do chat)
 * — só o PESO, que é o que a pizza desenha.
 */
import FeeLabKpiStrip from '@modules/fee-fixo/components/fee-lab-kpi-strip/fee-lab-kpi-strip.vue'
import FeeLabRegimeCards from '@modules/fee-fixo/components/fee-lab-regime-cards/fee-lab-regime-cards.vue'
import FeeLabProjecaoChart from '@modules/fee-fixo/components/fee-lab-projecao-chart/fee-lab-projecao-chart.vue'
import FeeLabEvolucaoPatrimonio from '@modules/fee-fixo/components/fee-lab-evolucao-patrimonio/fee-lab-evolucao-patrimonio.vue'
import FeeLabAlocacaoPizzas from '@modules/fee-fixo/components/fee-lab-alocacao-pizzas/fee-lab-alocacao-pizzas.vue'
import { CANAL_PADRAO, canalNormalizado } from '@modules/fee-fixo/constants/baldes'
import { alocacaoComparadaDoChat } from '@modules/fee-fixo/utils/alocacao-comparada-do-chat'
import {
  AVISO_SIMULACAO_SEM_REGISTRO,
  CTA_SALVAR_SIMULACAO,
  CTA_CANCELAR_SIMULACAO,
  CTA_TENTAR_DEPOSITO_DE_NOVO,
  MSG_CALCULO_NAO_FECHOU,
  TITULO_PREMISSAS_DA_SIMULACAO,
  TITULO_SIMULACAO_DO_CHAT,
} from '@modules/fee-fixo/constants/guia'
import { simulacaoDoChat } from '@modules/fee-fixo/utils/simulacao-do-chat'
import { linhasDasPremissasDeclaradas } from '@modules/fee-fixo/utils/premissas-declaradas'

/**
 * `MÓDULO · SEÇÃO` do vocabulário dos cartões (`ui-registry/cartao-casca.vue`).
 * Local, e não em `constants/guia.js`: quem o lê é só este cartão, e a segunda
 * morada seria a chance de o par sobretítulo/título divergir.
 */
const SOBRETITULO = 'Fee Fixo · Simulação'

export default {
  name: 'FeeFixoGuiaSimulacaoCard',
  components: {
    FeeLabKpiStrip,
    FeeLabRegimeCards,
    FeeLabProjecaoChart,
    FeeLabEvolucaoPatrimonio,
    FeeLabAlocacaoPizzas,
  },
  props: {
    /** O `S` dos quatro grupos, como a tool o devolveu (`contracts/s-contrato.md`). */
    inputs: {
      type: Object,
      required: true,
    },
    /**
     * O que a US-3 manda DIZER: as hipóteses do cenário em texto, para o
     * consultor saber sobre o que está decidindo. Vem pronto do backend — a
     * tela não redige premissa.
     *
     * `required` como o contrato manda, e não com default `{}`: a ausência é
     * DEFEITO (a US-3 obriga a declarar), e o default a tornaria invisível —
     * o cartão renderizaria sem as linhas e ninguém saberia. Com `required`, o
     * Vue avisa em dev e o `setup.js` transforma o aviso em teste vermelho.
     */
    premissasDeclaradas: {
      type: Object,
      required: true,
    },
    /** Sem ele, o Salvar não é oferecido (o servidor recusaria o commit). */
    previewId: {
      type: String,
      default: '',
    },
    /** Enquanto o commit está em voo — o segundo clique grava duas vezes. */
    processando: {
      type: Boolean,
      default: false,
    },
    /**
     * O aviso do BACKEND quando a simulação rodou sobre uma carteira mais
     * antiga que um upload pendente de confirmação (G-22 rodada 2). Vazio no
     * caso comum (carteira em dia) — string vinda pronta do `value`, o
     * cartão não redige nem resume.
     */
    avisoCarteiraPendente: {
      type: String,
      default: '',
    },
    /**
     * A falha do depósito AUTOMÁTICO do preview CORRENTE (vem da store, pelo
     * consumidor). Vazio no caso comum; cartão histórico nunca a recebe.
     */
    erroDeposito: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      textos: {
        sobretitulo: SOBRETITULO,
        titulo: TITULO_SIMULACAO_DO_CHAT,
        salvar: CTA_SALVAR_SIMULACAO,
        cancelar: CTA_CANCELAR_SIMULACAO,
        naoFechou: MSG_CALCULO_NAO_FECHOU,
        semRegistro: AVISO_SIMULACAO_SEM_REGISTRO,
        premissas: TITULO_PREMISSAS_DA_SIMULACAO,
        tentarDeNovo: CTA_TENTAR_DEPOSITO_DE_NOVO,
      },
    }
  },
  computed: {
    /** Motor + guarda numa decisão só (`utils/simulacao-do-chat.js`). */
    simulacao() {
      return simulacaoDoChat(this.inputs)
    },
    publicavel() {
      return this.simulacao.publicavel
    },
    resultado() {
      return this.simulacao.resultado
    },
    /** O `S` inteiro — é o que os dois componentes de gráfico consomem. */
    estado() {
      return this.simulacao.S
    },
    auc() {
      const auc = this.estado.auc
      return typeof auc === 'number' && Number.isFinite(auc) ? auc : 0
    },
    /**
     * O canal resolvido UMA vez, aqui — [D8]. Cada consumidor que repetisse
     * `canalNormalizado` seria mais um lugar onde tela e documento divergem.
     */
    canal() {
      return canalNormalizado(this.estado.canal) || CANAL_PADRAO
    },
    mostraEvolucao() {
      return this.simulacao.mostraEvolucao
    },
    /**
     * As duas alocações do cenário (hoje × fim da migração), classe a
     * classe — `null` quando `inputs.alocacao.alc_c`/`inputs.alloc` não
     * fecham. `null` some com o bloco inteiro no template (ver o
     * cabeçalho): nunca meia pizza.
     */
    alocacaoComparada() {
      return alocacaoComparadaDoChat(this.estado)
    },
    /**
     * As hipóteses em texto, na ordem do contrato.
     *
     * ★ RÓTULO + VALOR, e a tradução vive em `utils/premissas-declaradas.js`.
     * Até 24/08/2026 isto era `String(declaradas[chave] || '')` — a chave ia
     * fora e só o valor CRU chegava à tela: o QA leu
     * `1 / 0.1 / 0.01 / 5 / 5 / assessoria / fb / [object Object] / 60` ao lado
     * de um texto da IA que declarava as mesmas premissas corretamente. O
     * backend nunca mandou frases (medido: `_premissas_declaradas` devolve
     * campo → valor), e era a tela que precisava dizer a grandeza de cada um.
     */
    linhasDeclaradas() {
      return linhasDasPremissasDeclaradas(this.premissasDeclaradas)
    },
    podeSalvar() {
      return this.publicavel && Boolean(this.previewId) && !this.processando
    },
    /**
     * Sem `previewId` o Salvar não é oferecido — e agora o cartão DIZ por quê
     * (SHOULD da review, 24/08/2026). Só quando o cálculo fechou: sem números
     * publicáveis, a frase que vale é a do `nao-fechou`, e duas explicações
     * empilhadas competiriam pela mesma atenção.
     */
    semRegistro() {
      return this.publicavel && !this.previewId
    },
  },
  methods: {
    salvar() {
      // A guarda vive no MÉTODO também: o `disabled` cobre o clique, e este
      // caminho grava.
      if (!this.podeSalvar) return
      // Só o `previewId`: os números já estão no servidor pelo depósito, que
      // tem dono próprio — nenhum consumidor lê `resultados` do evento.
      this.$emit('confirmar', { previewId: this.previewId })
    },
    /** O retry do depósito automático — quem deposita é o consumidor. */
    redepositar() {
      this.$emit('redepositar', { previewId: this.previewId })
    },
  },
}
</script>

<template>
  <!-- G-6: `role`/`aria-label`/`aria-busy` como o irmão
  (`fee-fixo-guia-confirmacao.vue:179-181`). O cartão é um grupo com ações, e
  sem `aria-busy` o leitor de tela não sabe que a gravação está em curso. -->
  <div
    class="ia-block-card pa-4 ff-guia-simulacao"
    role="group"
    :aria-label="textos.titulo"
    :aria-busy="processando ? 'true' : 'false'"
  >
    <!-- ★ [owner, 19/09/2026] Cabeçalho no vocabulário dos cartões:
         sobretítulo `MÓDULO · SEÇÃO` + título em serif (`ia-titulo-de-cartao`).
         Era um rótulo miúdo com um glifo em `#8E8E8E` LITERAL, e o cartão abria
         direto no dashboard — na vitrine, o único sem cabeçalho.
         ⚠️ O glifo saiu junto: nos cartões do fee-fixo o ícone ao lado do
         título não entra (o sobretítulo já diz de onde o cartão é). -->
    <div class="mb-4">
      <span
        data-teste="simulacao-sobretitulo"
        class="az-ds-label d-block"
      >{{ textos.sobretitulo }}</span>
      <span
        data-teste="simulacao-titulo"
        class="ia-titulo-de-cartao d-block"
      >{{ textos.titulo }}</span>
    </div>

    <!-- ★ A guarda ANTES de desenhar: sem os valores em reais, nenhum número
    vai à tela. Os bps continuariam certos, e é justamente isso que faria o
    cenário parecer inteiro. -->
    <div
      v-if="!publicavel"
      data-teste="simulacao-nao-fechou"
      class="d-flex align-start px-4 py-3 ff-guia-simulacao__aviso"
      role="alert"
    >
      <icon-svg
        name="info-circle"
        :size="18"
        color="var(--text-muted)"
        class="mr-2 flex-shrink-0"
      />
      <p class="az-ds-body mb-0">{{ textos.naoFechou }}</p>
    </div>

    <template v-else>
      <!-- G-22 rodada 2: o aviso do BACKEND, ACIMA dos KPIs — é o número logo
      abaixo que ele qualifica. Texto verbatim, nunca reescrito pela tela. -->
      <BaseAlert
        v-if="avisoCarteiraPendente"
        data-teste="simulacao-aviso-carteira-pendente"
        status="warning"
        class="mb-4"
      >
        {{ avisoCarteiraPendente }}
      </BaseAlert>

      <FeeLabKpiStrip
        data-teste="simulacao-kpis"
        class="mb-4"
        :resultado="resultado"
        :auc="auc"
        :canal="canal"
      />

      <FeeLabRegimeCards
        data-teste="simulacao-regimes"
        class="mb-4"
        :resultado="resultado"
        :auc="auc"
        :canal="canal"
      />

      <!-- F5 (08/09/2026): mesma posição do PDF — depois da composição do
      custo, antes da evolução no tempo. Some INTEIRA sem as duas alocações
      fechando (ver o cabeçalho do arquivo). -->
      <FeeLabAlocacaoPizzas
        v-if="alocacaoComparada"
        data-teste="simulacao-alocacao"
        class="mb-4"
        :linhas="alocacaoComparada.linhas"
      />

      <!-- ★ G-1 do design-conformance. Este é o ÚNICO dos quatro blocos que
      não traz moldura própria — medido no browser: `0px none`, raio 0, fundo
      transparente, contra `1px solid rgb(236,236,239)` / raio 14 / padding 24
      dos outros três. Entre dois blocos emoldurados ele ficava solto e parecia
      texto da conversa. A moldura é a MESMA da tela, que envolve este
      componente em `az-card-bordered mb-4`
      (`view-fee-fixo-simulacao.vue:453`) — não é invenção do cartão.
      ⚠️ [owner, 19/09/2026] `no-shadow` junto: nenhum cartão do thread pinta
      sombra, e `az-card-bordered` sozinho traz `box-shadow: var(--shadow-sm)`,
      que é a elevação do card de PÁGINA. A regra geral do módulo
      (`aawz-intelligence-v2/design/_ia-block-card.sass`) não alcança este
      bloco porque a raiz deste cartão não é um `.ia-block-card` — ele é um
      `<div role="group">` sem chrome próprio. -->
      <div class="az-card-bordered no-shadow mb-4">
        <FeeLabProjecaoChart
          data-teste="simulacao-projecao"
          :resultado="resultado"
          :state="estado"
        />
      </div>

      <!-- Só com o grupo 4 completo. Sem ele o gráfico degrada para UM ponto,
      que afirma "este cliente não evolui" — pior que a ausência. -->
      <FeeLabEvolucaoPatrimonio
        v-if="mostraEvolucao"
        data-teste="simulacao-evolucao"
        class="mb-4"
        :state="estado"
      />

      <!-- ⚠️ `<ul>` dentro do shell do módulo: `.content-nav ul` o deita e
      `.content-nav ul li` dá 18px de margem a cada item (`_content-nav.sass`).
      Regra de ANCESTRAL — `scoped` não barra; o eixo vai no `<style>`. -->
      <!-- ★ G-2 do design-conformance. A US-3 torna este bloco OBRIGATÓRIO —
      ele existe para o consultor conferir as hipóteses antes de salvar —, e
      ele era o item de MENOR peso visual do cartão: um parágrafo cinza sem
      cabeçalho, entre um gráfico e dois botões. Ganhou o rótulo e o ritmo dos
      três irmãos (estado, confirmação e atalho): hairline entre itens e
      `padding: 12px 0`, com o primeiro sem linha em cima. -->
      <p data-teste="simulacao-premissas-titulo" class="az-ds-label mb-2">
        {{ textos.premissas }}
      </p>
      <ul class="ff-guia-simulacao__premissas mb-4">
        <li
          v-for="linha in linhasDeclaradas"
          :key="linha.chave"
          data-teste="simulacao-premissa"
          class="az-ds-aux ff-guia-simulacao__item"
        >
          {{ linha.rotulo }}: {{ linha.valor }}
        </li>
      </ul>
    </template>

    <div
      v-if="semRegistro"
      data-teste="simulacao-sem-registro"
      class="d-flex align-center az-text-primary az-size-12 mb-3 ff-guia-simulacao__bloqueio"
      role="alert"
    >
      <icon-svg
        name="warning"
        :size="14"
        color="var(--danger-text)"
        class="mr-1 flex-shrink-0"
      />
      {{ textos.semRegistro }}
    </div>

    <!-- G-7 (T037): o depósito automático é do captador (`apiFeeFixoIa`) — este
    bloco só aparece se ele FALHAR no preview corrente, e nunca em silêncio.
    `data-teste` no bloco e no botão de retry, mesmo padrão do bloco acima. -->
    <div
      v-if="erroDeposito"
      data-teste="simulacao-erro-deposito"
      class="d-flex align-center az-text-primary az-size-12 mb-3 ff-guia-simulacao__bloqueio"
      role="alert"
    >
      <icon-svg
        name="warning"
        :size="14"
        color="var(--danger-text)"
        class="mr-1 flex-shrink-0"
      />
      <span>{{ erroDeposito }}</span>
      <button
        type="button"
        class="az-ds-hyperlink ml-1"
        data-teste="simulacao-tentar-deposito-de-novo"
        @click="redepositar"
      >
        {{ textos.tentarDeNovo }}
      </button>
    </div>

    <div class="d-flex align-center justify-end az-gap-small">
      <!-- G-5: `:disabled` como o irmão (`confirmacao.vue:257`). Sem ele, o
      Cancelar seguia clicável durante a gravação. -->
      <BaseButton
        underlined
        fit-content
        data-teste="simulacao-cancelar"
        :disabled="processando"
        @click="$emit('cancelar')"
      >
        {{ textos.cancelar }}
      </BaseButton>

      <!-- Sem `previewId` o botão NÃO aparece: o servidor recusaria o commit, e
      oferecer o clique é prometer uma gravação que não acontece. O Cancelar
      continua vivo — é a saída que sobra. -->
      <BaseButton
        v-if="previewId"
        primary
        fit-content
        :disabled="!podeSalvar"
        :loading="processando"
        data-teste="simulacao-salvar"
        @click="salvar"
      >
        {{ textos.salvar }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.ff-guia-simulacao
  &__premissas
    display: flex
    flex-direction: column
    align-items: stretch
    list-style: none
    margin: 0
    padding: 0

    li
      margin-right: 0

  // ★ G-2. O ritmo dos três irmãos do thread — `fee-fixo-guia-estado-card`
  // (`&__item`), `-confirmacao` e `-cliente-atalho-card` usam exatamente
  // isto, e o comentário do atalho chama o estado-card de "precedente do
  // módulo para lista dentro de cartão". O primeiro não leva linha em cima:
  // ela separa itens, não abre o bloco.
  &__item
    padding: 12px 0
    border-top: 1px solid var(--border-hairline)
    &:first-child
      border-top: none
      padding-top: 0

  // ★ G-7. A guarda tinha `px-4 py-3` e NENHUMA moldura — 16px de recuo que
  // não fechavam em nada. A moldura é a que o resto do módulo usa para bloco
  // de aviso dentro de card.
  &__aviso
    border: 1px solid var(--border-hairline)
    border-radius: var(--radius-md)

  // ★ G-7. O aviso de registro pintava só o ícone de vermelho e deixava a
  // frase no cinza do corpo — o irmão pinta a linha inteira
  // (`&__bloqueio` em `fee-fixo-guia-confirmacao.vue:312`).
  &__bloqueio
    color: var(--danger-text)
</style>
